import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';

import './NewVersionPopup.css';
import { requestPlatformInformation } from '../flutter/DeviceInfo';

function isVersionLessThan(v1: string, v2: string) {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  const len = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < len; i++) {
    const a = parts1[i] || 0;
    const b = parts2[i] || 0;
    if (a < b) return true;
    if (a > b) return false;
  }

  return false; // equal
}

function needVersionCheck() {
  if (!window.platformInformation && !window.flutter_inappwebview) {
    return false;
  }
  const now = new Date();
  const today = now.toDateString();

  const lastRun = localStorage.getItem('lastVersionCheckTime');

  if (lastRun !== today) {
    localStorage.setItem('lastVersionCheckTime', today);
    return true;
  }
  return false;
}

export default function NewVewsionPopup() {
  const { t } = useTranslation();
  const [popupState, setPopupState] = useState<'off' | 'warning' | 'urgent'>('off');
  const [currentVersion, setCurrentVersion] = useState<string | undefined>();
  const [requiredVersion, setRequiredVersion] = useState<string | undefined>();

  async function checkVersion() {
    const platformInfo = await requestPlatformInformation();
    setCurrentVersion(platformInfo.app.version);
    const latestUrgentVersion = import.meta.env.VITE_LATEST_URGENT_APP_VERSION;
    if (latestUrgentVersion) {
      if (isVersionLessThan(platformInfo.app.version, latestUrgentVersion)) {
        setRequiredVersion(latestUrgentVersion);
        setPopupState('urgent');
        return;
      }
    }
    const latestVersion = import.meta.env.VITE_LATEST_APP_VERSION;
    if (!latestVersion) {
      return;
    }
    if (isVersionLessThan(platformInfo.app.version, latestVersion)) {
      setRequiredVersion(latestVersion);
      setPopupState('warning');
    }
  }

  useEffect(() => {
    if (needVersionCheck()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void checkVersion();
    }
  }, []);

  if (popupState === 'off') {
    return;
  }

  function onUpdate() {
    const userAgent = navigator.userAgent;
    if (/android/i.test(userAgent)) {
      window.open('market://details?id=hu.itrack.itrackmobile', '_blank');
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      window.open('https://apps.apple.com/app/id6446988003', '_blank');
    }
    console.warn('Unknown OS:', userAgent);
  }

  function onClose() {
    setPopupState('off');
  }

  if (popupState === 'urgent') {
    return (
      <>
        <div className="top-overlay urgent-overlay">
          <div className="update-popup">
            <Typography variant="h5" className="urgent-popup-title">
              {t('version.urgentTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('version.urgentDescription')}
              <br />
              {t('version.currentVersion', { currentVersion: currentVersion })}
              <br />
              {t('version.urgentVersion', { urgentVersion: requiredVersion })}
            </Typography>
            <div className="popup-buttons">
              <Button className="btn-urgent-update" onClick={onUpdate}>
                {t('version.updateButton')}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="top-overlay simple-overlay">
        <div className="update-popup">
          <Typography variant="h5" sx={{ mb: 1 }}>
            {t('version.title')}
          </Typography>
          <Typography>
            {t('version.description')}
            <br />
            {t('version.currentVersion', { currentVersion: currentVersion })}
            <br />
            {t('version.latestVersion', { latestVersion: requiredVersion })}
          </Typography>
          <div className="popup-buttons">
            <Button className="btn-later" onClick={onClose}>
              {t('version.laterButton')}
            </Button>
            <Button className="btn-update" onClick={onUpdate}>
              {t('version.updateButton')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
